import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    customerInfoProviderContextToken,
    gwI18nContextToken,
    urlsBuilderContextToken,
} from "@/context_tokens";
import { customerInfoProviderToken, modalManagerToken } from "@/tokens";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import {
    reviewsAuthApiClientToken,
    reviewsIconCollectionManagerToken,
} from "@apps/reviews/tokens";
import CanLeaveReviewQuery from "@apps/reviews/widgets/review_form_modal_widget/queries/CanLeaveReviewQuery";
import CanLeaveReviewService from "@apps/reviews/widgets/review_form_modal_widget/services/CanLeaveReviewService";
import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
import { I18nModule } from "@modules/i18n/I18nModule";
import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import {
    productsQueryToken,
    productsServiceToken,
} from "@modules/products/tokens";
import ShopifyAnalyticsManager from "@modules/shopify_analytics/ShopifyAnalyticsManager";
import ShopifyAnalyticsModule from "@modules/shopify_analytics/ShopifyAnalyticsModule";
import { container, injectable } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import AttributesQuery from "./queries/AttributesQuery";
import LeaveReviewEarningRuleQuery from "./queries/LeaveReviewEarningRuleQuery";
import LeaveReviewQuery from "./queries/LeaveReviewQuery";
import PurchasedProductsQuery from "./queries/PurchasedProductsQuery";
import RewardForReviewQuery from "./queries/RewardForReviewQuery";
import ReviewsFormModalWidgetComponent from "./ReviewsFormModalWidget.svelte";
import { ReviewsFormModalWidgetConfigInterface } from "./ReviewsFormModalWidgetConfig";
import AttributesService from "./services/AttributesService";
import LeaveReviewEarningRuleService from "./services/LeaveReviewEarningRuleService";
import {
    default as LeaveReviewService,
    default as ReviewsService,
} from "./services/LeaveReviewService";
import PurchasedProductsService from "./services/PurchasedProductsService";
import RewardForReviewService from "./services/RewardForReviewService";
import AttributesStore from "./stores/AttributesStore";
import LeaveReviewEarningRuleStore from "./stores/LeaveReviewEarningRuleStore";
import LeaveReviewStore from "./stores/LeaveReviewStore";
import PurchasedProductsStore from "./stores/PurchasedProductsStore";
import RewardForReviewStore from "./stores/RewardForReviewStore";

@injectable()
class ReviewsFormModalWidget implements WidgetInterface {
    private readonly modalManager: ModalManager;
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly leaveReviewStore: LeaveReviewStore;
    private readonly reviewsService: ReviewsService;
    private readonly leaveReviewQuery: LeaveReviewQuery;
    private readonly attributesService: AttributesService;
    private readonly attributesStore: AttributesStore;
    private readonly attributesQuery: AttributesQuery;
    private readonly productsService: ProductsServiceInterface;
    private readonly productsQuery: ProductsQuery;
    private readonly leaveReviewEarningRuleStore: LeaveReviewEarningRuleStore;
    private readonly leaveReviewEarningRuleService: LeaveReviewEarningRuleService;
    private readonly leaveReviewEarningRuleQuery: LeaveReviewEarningRuleQuery;
    private readonly rewardForReviewStore: RewardForReviewStore;
    private readonly rewardForReviewService: RewardForReviewService;
    private readonly rewardForReviewQuery: RewardForReviewQuery;
    private readonly purchasedProductsStore: PurchasedProductsStore;
    private readonly purchasedProductsService: PurchasedProductsService;
    private readonly purchasedProductsQuery: PurchasedProductsQuery;
    private readonly canLeaveReviewService: CanLeaveReviewService;
    private readonly canLeaveReviewQuery: CanLeaveReviewQuery;
    private readonly gwI18n: GwI18n;
    private readonly customerInfoProvider: CustomerInfoProviderInterface;
    private readonly shopifyAnalyticsManager: ShopifyAnalyticsManager | null;
    private readonly authenticationInfoProvider: AuthenticationInfoProviderInterface;

    constructor(
        private readonly config: ReviewsFormModalWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("ReviewsFormModalWidget is initialized", config);
        const authApiClient = container.resolve(reviewsAuthApiClientToken);

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );

        // Reviews
        this.leaveReviewStore = new LeaveReviewStore();
        this.reviewsService = new LeaveReviewService(
            this.leaveReviewStore,
            authApiClient,
            this.logger
        );
        this.leaveReviewQuery = new LeaveReviewQuery(this.leaveReviewStore);

        this.modalManager = container.resolve(modalManagerToken);

        // Attributes Lists
        this.attributesStore = new AttributesStore();
        this.attributesQuery = new AttributesQuery(this.attributesStore);
        this.attributesService = new AttributesService(
            this.attributesStore,
            authApiClient,
            this.logger
        );

        // Main Product
        this.productsService = container.resolve(productsServiceToken);
        this.productsQuery = container.resolve(productsQueryToken);
        this.productsService.subscribe();

        // LeaveReviewEarningRule
        this.leaveReviewEarningRuleStore = new LeaveReviewEarningRuleStore();
        this.leaveReviewEarningRuleQuery = new LeaveReviewEarningRuleQuery(
            this.leaveReviewEarningRuleStore
        );
        this.leaveReviewEarningRuleService = new LeaveReviewEarningRuleService(
            this.leaveReviewEarningRuleStore,
            authApiClient,
            this.logger
        );

        // RewardForReview
        this.rewardForReviewStore = new RewardForReviewStore();
        this.rewardForReviewQuery = new RewardForReviewQuery(
            this.rewardForReviewStore
        );
        this.rewardForReviewService = new RewardForReviewService(
            this.rewardForReviewStore,
            authApiClient,
            this.logger
        );

        // Purchased products
        this.purchasedProductsStore = new PurchasedProductsStore();
        this.purchasedProductsService = new PurchasedProductsService(
            this.purchasedProductsStore,
            authApiClient,
            this.logger
        );
        this.purchasedProductsQuery = new PurchasedProductsQuery(
            this.purchasedProductsStore
        );
        this.canLeaveReviewService = container.resolve(CanLeaveReviewService);
        this.canLeaveReviewQuery = container.resolve(CanLeaveReviewQuery);

        this.gwI18n = container.resolve(I18nModule).exports.i18n;

        this.customerInfoProvider = container.resolve(
            customerInfoProviderToken
        );

        this.shopifyAnalyticsManager = container.resolve(
            ShopifyAnalyticsModule
        ).exports.shopifyAnalyticsManager;

        this.authenticationInfoProvider =
            container.resolve(
                AuthenticationModule
            ).exports.authenticationInfoProvider;
    }

    async init(): Promise<this> {
        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "reviews.ReviewsFormModalWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.ReviewsFormModalWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "common.critical.Points",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "common.critical.Points"
            )
        );

        const element = this.config.target;
        let placeholder = element.querySelector<HTMLElement>(
            ".gw-reviews-form-modal-widget-placeholder"
        );

        if (!placeholder) {
            placeholder = document.createElement("div");
            placeholder.classList.add(
                "gw-reviews-form-modal-widget-placeholder"
            );
            placeholder.style.display = "block";
            element.appendChild(placeholder);
        }

        const shadowRoot =
            placeholder.shadowRoot ||
            placeholder.attachShadow({ mode: "open" });

        const customizationManager = container.resolve(CustomizationManager);

        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_FORM_MODAL_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );
        contextMap.set(
            urlsBuilderContextToken,
            container.resolve(CommonModule).exports.urlsBuilder
        );
        contextMap.set(gwI18nContextToken, this.gwI18n);
        contextMap.set(
            customerInfoProviderContextToken,
            this.customerInfoProvider
        );

        new ReviewsFormModalWidgetComponent({
            target: shadowRoot,
            props: {
                modalManager: this.modalManager,
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                leaveReviewService: this.reviewsService,
                leaveReviewQuery: this.leaveReviewQuery,
                attributesQuery: this.attributesQuery,
                attributesService: this.attributesService,
                productsService: this.productsService,
                leaveReviewEarningRuleQuery: this.leaveReviewEarningRuleQuery,
                leaveReviewEarningRuleService:
                    this.leaveReviewEarningRuleService,
                rewardForReviewQuery: this.rewardForReviewQuery,
                rewardForReviewService: this.rewardForReviewService,
                purchasedProductsQuery: this.purchasedProductsQuery,
                purchasedProductsService: this.purchasedProductsService,
                canLeaveReviewService: this.canLeaveReviewService,
                canLeaveReviewQuery: this.canLeaveReviewQuery,
                logger: this.logger,
                shopifyAnalyticsManager: this.shopifyAnalyticsManager,
                authenticationInfoProvider: this.authenticationInfoProvider,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
            context: contextMap,
        });

        return this;
    }
}

export default ReviewsFormModalWidget;
